import React, { Component, ReactNode, ChangeEvent } from 'react';
import InputLabel, {
  InputLabelProps as MuiInputLabelProps,
} from '@material-ui/core/InputLabel';
import MenuItem, {
  MenuItemProps as MuiMenuItemProps,
} from '@material-ui/core/MenuItem';
import FormControl, {
  FormControlProps as MuiFormControlProps,
} from '@material-ui/core/FormControl';
import MuiSelect, { SelectProps } from '@material-ui/core/Select';
import merge from 'lodash/merge';
import clsx from 'clsx';
import withStyles from '@material-ui/core/styles/withStyles';
import FormHelperText from '@material-ui/core/FormHelperText';

const StyledMenuItem = withStyles({
  root: {
    whiteSpace: 'normal',
    wordBreak: 'break-all',
    overflowWrap: 'break-word',
  },
})(MenuItem);

interface Choice {
  text: ReactNode;
  value: MuiMenuItemProps['value'];
  itemProps?: MuiMenuItemProps;
}

export interface Props extends SelectProps {
  choices: Choice[];
  includeBlank?: boolean;
  blankText?: string;
  FormControlProps?: MuiFormControlProps;
  InputLabelProps?: MuiInputLabelProps;
  MenuItemProps?: MuiMenuItemProps;
  helperText?: ReactNode;
  onChange?: (event: ChangeEvent<{ name?: string; value: unknown }>) => void;
}

interface State {
  value: SelectProps['value'];
  prevChoices: Choice[];
}

class Select extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      value: this._isValidValue(props.value) ? props.value : '',
      prevChoices: props.choices || [],
    };

    this.handleChange = this.handleChange.bind(this);
  }

  static getDerivedStateFromProps(props, state) {
    const { value, choices = [] } = props;
    const { prevChoices = [] } = state;
    // Update value when different choices is assigned and includes props' value
    if (
      props.choices !== prevChoices &&
      (choices.length !== prevChoices.length ||
        choices.some(
          ({ text, value }) =>
            prevChoices.findIndex(
              ({ text: prevText, value: prevValue }) =>
                prevText === text && value === prevValue,
            ) === -1,
        )) &&
      Select.includes(choices, value)
    ) {
      return { value, prevChoices: choices };
    }
    return null;
  }

  static includes(choices, value) {
    return (
      choices?.findIndex(({ value: choiceVal }) => choiceVal == value) > -1
    );
  }

  _isValidValue(value) {
    const { includeBlank, choices = [] } = this.props;
    return (
      value !== this.state?.value &&
      ((includeBlank && value === '') ||
        (value != null && Select.includes(choices, value)))
    );
  }

  handleChange(event) {
    if (
      this._isValidValue(event.target.value) ||
      (this.props.includeBlank && event.target.value === '')
    ) {
      this.setState({ value: event.target.value });
      this.props.onChange?.(event);
    }
  }

  validate(value = this.state.value) {
    const { FormControlProps } = this.props;
    return !FormControlProps?.required || !!value;
  }

  render() {
    const {
      choices = [],
      includeBlank,
      blankText,
      labelId,
      label,
      FormControlProps,
      InputLabelProps,
      MenuProps,
      MenuItemProps = {},
      fullWidth,
      required,
      helperText,
      ...other
    } = this.props;
    const { value } = this.state;

    return (
      <FormControl
        {...{ fullWidth, required, ...FormControlProps }}
        size="small"
      >
        <InputLabel id={labelId} {...InputLabelProps}>
          {label}
        </InputLabel>
        <MuiSelect
          {...other}
          {...{ label, labelId, value, fullWidth }}
          onChange={this.handleChange}
          MenuProps={{
            PaperProps: { style: { maxHeight: 300 } },
            ...MenuProps,
          }}
        >
          {includeBlank && (
            <StyledMenuItem {...MenuItemProps} button value="">
              <em style={{ ...(!blankText && { visibility: 'hidden' }) }}>
                {blankText || '-'}
              </em>
            </StyledMenuItem>
          )}
          {choices.map(({ text, value, itemProps = {} }, i) => (
            <StyledMenuItem
              {...merge({}, MenuItemProps, itemProps, {
                className: clsx(MenuItemProps?.className, itemProps?.className),
              })}
              button
              key={`select-menu-${i}`}
              value={value}
            >
              {text}
            </StyledMenuItem>
          ))}
        </MuiSelect>
        {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }
}

export default Select;
